import { AxiosResponse } from 'axios';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { webSocketService } from '../../../services/web-socket.service';
import { HandleTableChange, ITableParams, RootStore } from '../../../stores';
import { ChoiceTypeEnum, GameStatusEnum, GameTypeEnum, IGame, IGameItem } from './game.types';
import { handleCommonTableChange, initTableParams } from '../../../utils/handleCommonTableChange';
import apiRoutes from '../../../config/api-routes';

const initKnbGame = {
  type: GameTypeEnum.KNB,
  choice: ChoiceTypeEnum.DICE,
  count: 1,
  amount: 0.1,
};

const initHotGame = {
  ...initKnbGame,
  type: GameTypeEnum.HOT,
};

class GameStore {
  public rootStore: RootStore;

  public newGame: IGame;
  public listGames: IGameItem[] = [];
  public total = 0;
  public getGamesParams: ITableParams & { minBetAmount?: number; maxBetAmount?: number };
  public selectedChoice: any = {};
  public userBalance: string | undefined;
  public isGameModal = false;
  public isCreateGameModal = false;
  public selectedGame?: IGameItem;
  public showLastGames = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.newGame = initKnbGame;
    this.getGamesParams = initTableParams;

    makeAutoObservable(this, undefined, { autoBind: true });
  }

  afterCreate() {
    reaction(
      () => this.rootStore.requestError,
      requestError => {
        if (requestError) {
          this.rootStore.notifications.error(requestError);
        }
      },
    );

    reaction(
      () => this.rootStore.user.isConnectedWS,
      isConnected => {
        if (isConnected) {
          webSocketService.on('games-list', (e: any) => {
            console.log('WebSocket games-list');
            const response = JSON.parse(e);
            console.log(response);
            if (!this.showLastGames) {
              this.listGames = this._setListGames(response.games);
            }

            this.userBalance = `${response.balance}(${response.lockedBalance})`;
            this.total = response.total;
          });
        }
      },
    );
  }

  public async create() {
    this.rootStore.request({
      method: 'post',
      route: apiRoutes.gamesCreate,
      params: {
        type: this.newGame.type,
        choice: this.newGame.choice,
        countGames: this.newGame.count,
        betAmount: this.newGame.amount,
      },
      succesCode: 201,
      success: () => {
        this.rootStore.notifications.success('Игры созданы!');
        this.closeCreateGameModal();
        this.setInitGame(this.newGame.type);
      },
      errorMessage: 'Create games failed',
    });
  }

  public async update(gameId?: string) {
    if (!gameId) return;

    const game = this.listGames.find(i => i.gameId === gameId);
    console.log(game);
    this.rootStore.request({
      method: 'post',
      route: apiRoutes.gamesUpdate,
      params: {
        gameId,
        choice: game?.isOwner ? undefined : game?.selectedChoice,
      },
      success: (response: AxiosResponse) => {
        this.rootStore.notifications.success(response.data.message);
        this.isGameModal = false;
      },
      errorMessage: 'Update games failed',
    });
  }

  public updateChoiceGame(gameId?: string, choice?: ChoiceTypeEnum) {
    if (!gameId || !choice) return;
    runInAction(() => {
      this.selectedChoice[gameId] = choice;
    });

    this.listGames = this._setListGames(this.listGames);

    console.log(this.selectedChoice);
  }

  public async getLastGames() {
    await this.rootStore.request({
      method: 'get',
      route: apiRoutes.gamesLast,
      params: { type: this.newGame.type },
      success: (response: AxiosResponse) => {
        this.showLastGames = true;
        this.getGamesParams.limit = 100;
        this.listGames = this._setListGames(response.data.games);
      },
      errorMessage: 'Get last games failed',
    });
  }

  public showCurrentGames() {
    this.showLastGames = false;
    this.getGamesParams = initTableParams;

    webSocketService.emit('get-games', { ...this.getGamesParams, type: this.newGame.type });
  }

  handleTableChange: HandleTableChange<IGameItem> = (pagination, filters, sorter, extra) => {
    handleCommonTableChange(pagination, sorter, this.getGamesParams, () =>
      webSocketService.emit('get-games', { ...this.getGamesParams, type: this.newGame.type }),
    );
  };

  public setFilterAmount = ({ minBetAmount, maxBetAmount }: { minBetAmount: number; maxBetAmount: number }) => {
    if (minBetAmount >= 0 && maxBetAmount >= 0) {
      this.getGamesParams.minBetAmount = minBetAmount;
      this.getGamesParams.maxBetAmount = maxBetAmount;
      webSocketService.emit('get-games', { ...this.getGamesParams, type: this.newGame.type });
    }
  };

  public setCountGames = (value: number) => {
    if (value !== this?.newGame?.count) {
      runInAction(() => {
        this.newGame.count = value;
      });
    }
  };

  public setAmountGames = (value: number | null) => {
    if (value) {
      const roundedValue = Math.round(value * 10) / 10;
      if (roundedValue !== this?.newGame?.amount) {
        runInAction(() => {
          this.newGame.amount = roundedValue;
        });
      }
    }
  };

  public updateNewGame = (type?: ChoiceTypeEnum) => {
    if (type && type !== this?.newGame?.choice) {
      runInAction(() => {
        this.newGame.choice = type;
      });
    }
  };

  public setInitGame(type: GameTypeEnum) {
    const newGame = type === GameTypeEnum.KNB ? initKnbGame : initHotGame;

    runInAction(() => {
      this.newGame = newGame;
    });
  }

  public setDefaultParams(type: GameTypeEnum) {
    this.newGame.type = type;
    this.newGame.choice = ChoiceTypeEnum.DICE;
    this.getGamesParams = initTableParams;

    webSocketService.emit('get-games', { ...this.getGamesParams, type: this.newGame.type });
  }

  public showGameModal(game: IGameItem) {
    this.selectedGame = game;
    this.isGameModal = true;
  }

  public closeGameModal() {
    this.isGameModal = false;
  }

  public showCreateGameModal() {
    this.isCreateGameModal = true;
  }

  public closeCreateGameModal() {
    this.isCreateGameModal = false;
  }

  private _setListGames(list: IGameItem[]) {
    return list.map(i => {
      const result = this._getResultData(i);
      return {
        ...i,
        selectedChoice: i.isOwner || i.winnerId ? undefined : this.selectedChoice[i.gameId] || ChoiceTypeEnum.DICE,
        colorResult: result?.color,
        titleResult: result?.title,
      };
    });
  }

  private _getResultData(game: IGameItem): { color: string; title: string } | undefined {
    if (game.status === GameStatusEnum.CANCELED) {
      return { color: '#d9d9d9', title: 'ОТМЕНЕНА' };
    }

    if (game.status === GameStatusEnum.FINISHED) {
      if (!game.winnerId) {
        return { color: game.isMember ? '#2563EB' : '#d9d9d9', title: 'НИЧЬЯ' };
      }

      if (game.isWinner) {
        return { color: '#48AB93', title: 'ПОБЕДА' };
      }

      if (game.isMember) {
        return { color: '#E55A53', title: 'ПРОИГРАЛ' };
      }

      return { color: '#d9d9d9', title: `ПОБЕДИЛ` };
    }

    return undefined;
  }
}

export default GameStore;
